import React, { useEffect, useRef } from "react";
import { sendClickEvent, sendScrollEvent } from "./analyticsEvents";
import { sendFormSubmitEvent } from './sendFormSubmitEvent';
import { useSiteData } from "../SiteDataProvider";
import { CANVAS_CONFIG, CLICK_SELECTORS, DEFAULT_LABELS, DESCRIPTION_TEMPLATES, HTML_ATTRIBUTES, NAVIGATION_DELAY_MS, SCROLL_THRESHOLDS, STORAGE_KEYS } from "./constant";
import { logger } from "./analyticsLogger";

const GlobalClickListener: React.FC = () => {
  const { data } = useSiteData();
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    const handleClick = async (event: MouseEvent) => {
      const target = event.target as HTMLElement;


      const trackedElement = target.closest(CLICK_SELECTORS) as HTMLElement | null;

      if (!trackedElement) return;

      const label =
        trackedElement.getAttribute(HTML_ATTRIBUTES.ANALYTICS_LABEL) ||
        trackedElement.innerText ||
        trackedElement.getAttribute(HTML_ATTRIBUTES.ARIA_LABEL) ||
        trackedElement.getAttribute(HTML_ATTRIBUTES.ARIA_LABELLEDBY) ||
        DEFAULT_LABELS.UNKNOWN;

      const orderIdAttr = trackedElement.getAttribute(HTML_ATTRIBUTES.ORDER);
      const orderIdNum = orderIdAttr !== null ? Number(orderIdAttr) : NaN;
      const orderId = !isNaN(orderIdNum) ? orderIdNum : undefined;

      const blockName =
        trackedElement.getAttribute(HTML_ATTRIBUTES.BLOCK_NAME) ||
        trackedElement.getAttribute(HTML_ATTRIBUTES.BLOCK_NAME_LOWER) ||
        undefined;

      const sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID) || undefined;

      // --- NEW: detect form submit buttons/inputs ---
      const tag = trackedElement.tagName.toLowerCase();
      const isSubmit =
        (tag === "button" && (trackedElement as HTMLButtonElement).type === "submit" && trackedElement.innerHTML !== DEFAULT_LABELS.JOIN_US) ||
        (tag === "input" && (trackedElement as HTMLInputElement).type === "submit");

      if (isSubmit) {
        const formEl = trackedElement.closest("form") as HTMLFormElement | null;
        const formName = formEl?.getAttribute("name") || formEl?.id || "Contact us Form";

        await sendFormSubmitEvent(formName, {
          source: CANVAS_CONFIG.SOURCE,
          type: CANVAS_CONFIG.TYPES.COMPONENT,
          id: `${blockName || 'Unknown'}-${orderId ?? ''}`,
          component: blockName || formName,
          name: formName || 'Unknown',
          action: CANVAS_CONFIG.ACTIONS.BUTTON_CLICKED,
          label: DEFAULT_LABELS.SUBMIT,
          position: `${orderId ?? ''}`,
        });
        return;
      }


      if (trackedElement.tagName.toLowerCase() === "a") {
        const anchor = trackedElement as HTMLAnchorElement;
        if (anchor.href) {
          event.preventDefault();

          await sendClickEvent({
            eventLabel: label,
            orderId,
            blockName,
            sessionId,
            attribute: {
              description: DESCRIPTION_TEMPLATES.buttonClicked(label),
              pagename: document.title || window.location.pathname,
            },
            m_canvas: {
              source: CANVAS_CONFIG.SOURCE,
              type: CANVAS_CONFIG.TYPES.COMPONENT,
              id: `${blockName || DEFAULT_LABELS.UNKNOWN}-${orderId ?? ''}`,
              component: blockName || DEFAULT_LABELS.UNKNOWN,
              name: blockName || DEFAULT_LABELS.UNKNOWN,
              action: CANVAS_CONFIG.ACTIONS.BUTTON_CLICKED,
              label: label,
              position: `${orderId ?? ''}`,
            },
          });

          setTimeout(() => {
            if (anchor.target === "_blank") {
              window.open(anchor.href, "_blank");
            } else {
              window.location.href = anchor.href;
            }
          }, NAVIGATION_DELAY_MS);
          return;
        }
      }

      await sendClickEvent({
        eventLabel: label,
        orderId,
        blockName,
        sessionId,
        attribute: {
          description: DESCRIPTION_TEMPLATES.buttonClicked(label),
          pagename: document.title || window.location.pathname,
        },
        m_canvas: {
          source: CANVAS_CONFIG.SOURCE,
          type: CANVAS_CONFIG.TYPES.COMPONENT,
          id: `${blockName || DEFAULT_LABELS.UNKNOWN}-${orderId ?? ''}`,
          component: blockName || DEFAULT_LABELS.UNKNOWN,
          name: blockName || DEFAULT_LABELS.UNKNOWN,
          action: CANVAS_CONFIG.ACTIONS.BUTTON_CLICKED,
          label: label,
          position: `${orderId ?? ''}`,
        },
      });
    };

    const triggeredThresholds: Set<number> = new Set();

    const trackScrollDepth = async () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      const pageId = dataRef.current?.pageIdAttachedWithSlug;
      const sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID)  || undefined;

      for (const threshold of SCROLL_THRESHOLDS) {
        if (scrollPercent >= threshold && !triggeredThresholds.has(threshold)) {
          triggeredThresholds.add(threshold);

          // Fire and forget - non-blocking
          await sendScrollEvent({
            scrollDepthPercent: threshold,
            pageId,
            sessionId,
          });
          logger.debug('Analytics', `Scroll depth tracked: ${threshold}%`);
        }
      }
    };

    const scrollHandler = () => {
      void requestAnimationFrame(trackScrollDepth);
    };

    window.addEventListener("click", handleClick, true);
    window.addEventListener("scroll", scrollHandler);

    return () => {
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);


  return null;
};

export default GlobalClickListener;